import cx from "classnames";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";

import { TicketStatusCode } from "@gamedev-web/one-gaming-crm";
import { ButtonType, HeroButton } from "@gaming/xds-react-js";

import * as commonStyles from "../../components/commonStyles/commonStyles.module.css";
import { SelectDropdown } from "../../components/SelectDropdown";
import { LoadingSpinner } from "../../components/Spinner/Spinner";
import { PageType } from "../../constants";
import {
  AddCommentRequest,
  AttachmentInput,
  TicketCommentAuthor,
  TicketStatusLabel,
  TranslationStatus,
  UpdateTicketRequest,
} from "../../models/ticketing";
import { RouteNames } from "../../partnerConfig/routeNames";
import { OnboardingUrls } from "../../partnerConfig/routes";
import { ticketingActions } from "../../redux/actions/ticketingActions";
import {
  getIsTicketingAdmin,
  getTicketingIsAddingComment,
  getTicketingIsDetailLoading,
  getTicketingIsUpdating,
  getTicketingSelectedTicket,
  getTicketingTeams,
  getTicketingTranslationMap,
} from "../../redux/selectors/ticketingSelectors";
import { AccessDeniedPage } from "../AccessDeniedPage/AccessDeniedPage";
import * as detailStyles from "./TicketDetailPage.module.css";
import { useTicketingStrings } from "./ticketingStrings";
import * as pageStyles from "./TicketingTicketDetailPage.module.css";
import {
  translateSeverityLabel,
  translateStatusLabel,
  translateValue,
  TranslationEntity,
  TranslationField,
} from "./translateValue";
import { useRequireTicketingAccess } from "./useRequireTicketingAccess";

type TicketDetailParams = {
  sellerId: string;
  ticketNumber: string;
};

const formatLocalDateTime = (iso: string | undefined): string => {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  if (isNaN(d.getTime())) {
    return iso;
  }
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  const year = d.getFullYear();
  const rawHours = d.getHours();
  const period = rawHours >= 12 ? "PM" : "AM";
  const hours = rawHours % 12 || 12;
  const minutes = String(d.getMinutes()).padStart(2, "0");
  return `${month}/${day}/${year} ${hours}:${minutes} ${period}`;
};

const isHtml = (text: string): boolean => /<[a-z][\s\S]*>/i.test(text);

const hasTranslation = (status: number | undefined): boolean =>
  status === TranslationStatus.AutoTranslated || status === TranslationStatus.HumanReviewed;

const TranslationBadge: React.FC<{
  status: number | undefined;
  showingOriginal: boolean;
  onToggle: () => void;
  strings: {
    ticketTranslationAutoLabel: string;
    ticketTranslationHumanLabel: string;
    ticketTranslationSeeOriginal: string;
    ticketTranslationSeeTranslation: string;
  };
}> = ({ status, showingOriginal, onToggle, strings }) => {
  if (status === TranslationStatus.AutoTranslated) {
    return (
      <span className={detailStyles.translationBadgeGroup}>
        <span className={detailStyles.translationBadge} title={strings.ticketTranslationAutoLabel}>
          🤖 {strings.ticketTranslationAutoLabel}
        </span>
        <button className={detailStyles.translationToggle} type="button" onClick={onToggle}>
          {showingOriginal
            ? strings.ticketTranslationSeeTranslation
            : strings.ticketTranslationSeeOriginal}
        </button>
      </span>
    );
  }
  if (status === TranslationStatus.HumanReviewed) {
    return (
      <span className={detailStyles.translationBadgeGroup}>
        <span className={detailStyles.translationBadge} title={strings.ticketTranslationHumanLabel}>
          ✅ {strings.ticketTranslationHumanLabel}
        </span>
        <button className={detailStyles.translationToggle} type="button" onClick={onToggle}>
          {showingOriginal
            ? strings.ticketTranslationSeeTranslation
            : strings.ticketTranslationSeeOriginal}
        </button>
      </span>
    );
  }
  return null;
};

const commentQuillModules = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const commentQuillFormats = [
  "bold",
  "italic",
  "underline",
  "strike",
  "color",
  "background",
  "list",
  "indent",
  "align",
  "script",
  "link",
];

let linkSanitizeConfigured = false;

const focusableSelector =
  'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

const moveFocusOutside = (container: HTMLElement, reverse: boolean) => {
  const scope = container.closest<HTMLElement>("main") ?? document.documentElement;
  const allFocusable = Array.from(scope.querySelectorAll<HTMLElement>(focusableSelector));
  const firstIdx = allFocusable.findIndex(el => container.contains(el));
  let lastIdx = firstIdx;
  for (let i = allFocusable.length - 1; i >= firstIdx; i--) {
    if (container.contains(allFocusable[i])) {
      lastIdx = i;
      break;
    }
  }

  if (reverse) {
    const target =
      firstIdx > 0 ? allFocusable[firstIdx - 1] : allFocusable[allFocusable.length - 1];
    target?.focus();
  } else {
    const target = lastIdx < allFocusable.length - 1 ? allFocusable[lastIdx + 1] : allFocusable[0];
    target?.focus();
  }
};

export const TicketingTicketDetailPage: React.FC = () => {
  const { isSignedIn, isVerifyChecked, isVerified } = useRequireTicketingAccess();
  const strings = useTicketingStrings();
  const dispatch = useDispatch();
  const history = useHistory();
  const { sellerId, ticketNumber } = useParams<TicketDetailParams>();
  const ticket = useSelector(getTicketingSelectedTicket);
  const isLoading = useSelector(getTicketingIsDetailLoading);
  const isAddingComment = useSelector(getTicketingIsAddingComment);
  const isUpdating = useSelector(getTicketingIsUpdating);
  const translationMap = useSelector(getTicketingTranslationMap);
  const isAdmin = useSelector(getIsTicketingAdmin);
  const teams = useSelector(getTicketingTeams);

  const [commentText, setCommentText] = useState("");
  const [commentTextLength, setCommentTextLength] = useState(0);
  const [newTag, setNewTag] = useState("");
  const [selectedTeamId, setSelectedTeamId] = useState<string>("");
  const [isSavingTeamScope, setIsSavingTeamScope] = useState(false);

  const [ReactQuill, setReactQuill] = useState<React.ComponentType<Record<string, unknown>> | null>(
    null
  );

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    Promise.all([import("react-quill-new"), import("react-quill-new/dist/quill.snow.css")]).then(
      ([mod]) => {
        if (!linkSanitizeConfigured) {
          const Quill = mod.Quill;
          const Link = Quill.import("formats/link") as { sanitize: (url: string) => string };
          const origSanitize = Link.sanitize.bind(Link);
          Link.sanitize = (url: string): string => {
            if (url && !/^https?:\/\//i.test(url) && !/^mailto:/i.test(url)) {
              return origSanitize(`https://${url}`);
            }
            return origSanitize(url);
          };
          linkSanitizeConfigured = true;
        }
        setReactQuill(() => mod.default as unknown as React.ComponentType<Record<string, unknown>>);
      }
    );
  }, []);

  const commentEditorRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const wrapper = commentEditorRef.current;
    if (!wrapper) {
      return;
    }
    const handleTab = (e: KeyboardEvent) => {
      if (e.key === "Tab") {
        const editor = wrapper.querySelector(".ql-editor");
        if (editor?.contains(document.activeElement)) {
          e.preventDefault();
          e.stopPropagation();
          moveFocusOutside(wrapper, e.shiftKey);
        }
      }
    };
    wrapper.addEventListener("keydown", handleTab, true);
    return () => wrapper.removeEventListener("keydown", handleTab, true);
  }, [isLoading]);

  const [localTags, setLocalTags] = useState<string[]>([]);
  const [tagsModified, setTagsModified] = useState(false);
  const [newAttachmentUrl, setNewAttachmentUrl] = useState("https://");
  const [newAttachmentDesc, setNewAttachmentDesc] = useState("");
  const [pendingAddAttachments, setPendingAddAttachments] = useState<AttachmentInput[]>([]);
  const [pendingRemoveIds, setPendingRemoveIds] = useState<string[]>([]);
  const [isClosing, setIsClosing] = useState(false);
  const [showOriginalResolution, setShowOriginalResolution] = useState(false);
  const [showOriginalComments, setShowOriginalComments] = useState<Record<string, boolean>>({});

  // Fetch ticket detail on mount
  useEffect(() => {
    if (!isSignedIn) {
      return;
    }
    if (ticketNumber) {
      dispatch(ticketingActions.fetchDetail.start(ticketNumber));
    }
    if (sellerId) {
      dispatch(ticketingActions.fetchOptions.start(sellerId));
      dispatch(ticketingActions.fetchTeamsAdmin.start(sellerId));
    }
    return () => {
      dispatch(ticketingActions.clearDetail());
    };
  }, [dispatch, ticketNumber, sellerId, isSignedIn]);

  const loadedTicketIdRef = useRef<string | undefined>(undefined);

  React.useEffect(() => {
    if (ticket && ticket.id !== loadedTicketIdRef.current) {
      loadedTicketIdRef.current = ticket.id;
      const parsed = ticket.tags
        ? ticket.tags
            .split(",")
            .map(t => t.trim())
            .filter(Boolean)
        : [];
      setLocalTags(parsed);
      setTagsModified(false);
      setPendingAddAttachments([]);
      setPendingRemoveIds([]);
      setNewAttachmentUrl("https://");
      setNewAttachmentDesc("");
    }
    if (!ticket) {
      loadedTicketIdRef.current = undefined;
    }
  }, [ticket]);

  React.useEffect(() => {
    if (!isUpdating) {
      setIsClosing(false);
      setPendingAddAttachments([]);
      setPendingRemoveIds([]);
      setNewAttachmentUrl("https://");
      setNewAttachmentDesc("");
    }
  }, [isUpdating]);

  const handleBack = useCallback(() => {
    const ticketingUrl = OnboardingUrls[RouteNames.Ticketing];
    history.push(ticketingUrl);
  }, [history]);

  // --- Tags ---
  const handleRemoveTag = useCallback((index: number) => {
    setLocalTags(prev => prev.filter((_, i) => i !== index));
    setTagsModified(true);
  }, []);

  const handleAddTag = useCallback(() => {
    const trimmed = newTag.trim();
    if (trimmed && !localTags.includes(trimmed)) {
      setLocalTags(prev => [...prev, trimmed]);
      setNewTag("");
      setTagsModified(true);
    }
  }, [newTag, localTags]);

  const handleTagKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        handleAddTag();
      }
    },
    [handleAddTag]
  );

  const handleSaveTags = useCallback(() => {
    if (!ticket || !tagsModified) {
      return;
    }
    const request: UpdateTicketRequest = {
      ticketId: ticket.id,
      tags: localTags.join(", "),
    };
    dispatch(ticketingActions.updateTicket.start(request));
    setTagsModified(false);
  }, [ticket, localTags, tagsModified, dispatch]);

  // --- Attachments ---
  const handleAddPendingAttachment = useCallback(() => {
    const trimmed = newAttachmentUrl.trim();
    if (trimmed && trimmed !== "https://") {
      setPendingAddAttachments(prev => [
        ...prev,
        { url: trimmed, description: newAttachmentDesc.trim() },
      ]);
      setNewAttachmentUrl("https://");
      setNewAttachmentDesc("");
    }
  }, [newAttachmentUrl, newAttachmentDesc]);

  const handleRemovePendingAdd = useCallback((index: number) => {
    setPendingAddAttachments(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleRemoveExistingAttachment = useCallback((attachmentId: string) => {
    setPendingRemoveIds(prev => [...prev, attachmentId]);
  }, []);

  const handleUndoRemoveAttachment = useCallback((attachmentId: string) => {
    setPendingRemoveIds(prev => prev.filter(id => id !== attachmentId));
  }, []);

  const attachmentsModified = pendingAddAttachments.length > 0 || pendingRemoveIds.length > 0;

  const handleSaveAttachments = useCallback(() => {
    if (!ticket || !attachmentsModified) {
      return;
    }
    const request: UpdateTicketRequest = {
      ticketId: ticket.id,
    };
    if (pendingRemoveIds.length > 0) {
      request.removeAttachmentIds = pendingRemoveIds;
    }
    if (pendingAddAttachments.length > 0) {
      request.addAttachments = pendingAddAttachments;
    }
    dispatch(ticketingActions.updateTicket.start(request));
  }, [ticket, pendingRemoveIds, pendingAddAttachments, attachmentsModified, dispatch]);

  // --- Status ---
  const isClosed = ticket?.status === TicketStatusLabel.Closed;
  const isResolved = ticket?.status === TicketStatusLabel.Resolved;

  const handleCloseTicket = useCallback(() => {
    if (!ticket || isClosed) {
      return;
    }
    setIsClosing(true);
    const request: UpdateTicketRequest = {
      ticketId: ticket.id,
      statusCode: TicketStatusCode.closed,
    };
    dispatch(ticketingActions.updateTicket.start(request));
  }, [ticket, isClosed, dispatch]);

  const handleSaveTeamScope = useCallback(() => {
    if (!ticket || !selectedTeamId || selectedTeamId === ticket.teamId) {
      return;
    }
    setIsSavingTeamScope(true);
    const request: UpdateTicketRequest = {
      ticketId: ticket.id,
      teamId: selectedTeamId,
    };
    dispatch(ticketingActions.updateTicket.start(request));
  }, [ticket, selectedTeamId, dispatch]);

  useEffect(() => {
    if (!isUpdating && isSavingTeamScope) {
      setIsSavingTeamScope(false);
      if (ticketNumber) {
        dispatch(ticketingActions.fetchDetail.start(ticketNumber));
      }
    }
  }, [isUpdating, isSavingTeamScope, ticketNumber, dispatch]);

  // --- Comments ---
  const handleCommentChange = useCallback(
    (value: string, _delta: unknown, _source: unknown, editor: { getText: () => string }) => {
      setCommentText(value);
      setCommentTextLength(editor.getText().length - 1);
    },
    []
  );

  const handleSendComment = useCallback(() => {
    if (commentTextLength === 0 || !ticket) {
      return;
    }
    const request: AddCommentRequest = {
      ticketId: ticket.id,
      comment: commentText,
    };
    dispatch(ticketingActions.addComment.start(request));
    setCommentText("");
    setCommentTextLength(0);
  }, [commentText, commentTextLength, ticket, dispatch]);

  const visibleAttachments = ticket
    ? ticket.attachments.filter(a => !pendingRemoveIds.includes(a.id))
    : [];

  if (!isSignedIn || !isVerifyChecked) {
    return (
      <>
        <div className={pageStyles.outerContainer}>
          <div className={pageStyles.spinnerContainer}>
            <LoadingSpinner />
          </div>
        </div>
      </>
    );
  }

  if (!isVerified) {
    return (
      <AccessDeniedPage
        footNote={strings.ticketAccessDeniedMsg}
        pageType={PageType.IdXboxApplication}
      />
    );
  }

  return (
    <>
      <div className={pageStyles.outerContainer}>
        <div className={pageStyles.innerContainer}>
          <button className={pageStyles.backLink} onClick={handleBack}>
            ← {strings.ticketBackToList}
          </button>
          {isLoading || !ticket ? (
            <div className={pageStyles.container}>
              <div
                className={detailStyles.loadingContainer}
                role="status"
                aria-label={strings.ticketLoadingDetailsLabel}
              >
                <LoadingSpinner />
              </div>
            </div>
          ) : (
            <>
              {/* Header - outside white card */}
              <div className={detailStyles.modalHeader}>
                <div className={detailStyles.modalHeaderTitleRow}>
                  {ticket.ticketNumber && (
                    <h2 className={detailStyles.ticketNumber}>
                      {strings.ticketNumberPrefix}
                      {ticket.ticketNumber}
                    </h2>
                  )}
                  <span className={detailStyles.modalHeaderTitle} title={ticket.title ?? ""}>
                    {ticket.title ?? ""}
                  </span>
                </div>
                <span className={pageStyles.teamLabel}>
                  {strings.ticketTeamLabel}:
                  <span className={pageStyles.teamName}>
                    {ticket.teamName || strings.ticketTeamPrivateOption}
                  </span>
                </span>
              </div>

              {/* Body - inside white card */}
              <div className={pageStyles.container}>
                <div
                  className={detailStyles.modalBody}
                  style={{ maxHeight: "none", overflow: "visible" }}
                >
                  {/* Description */}
                  <div className={detailStyles.descriptionSection}>
                    <h3 className={detailStyles.sectionTitle}>
                      {strings.ticketDetailDescriptionLabel}
                    </h3>
                    {isHtml(ticket.description) ? (
                      <div
                        className={detailStyles.descriptionHtml}
                        dangerouslySetInnerHTML={{ __html: ticket.description }}
                      />
                    ) : (
                      <p className={detailStyles.descriptionText}>{ticket.description}</p>
                    )}
                  </div>

                  {/* Resolution Summary */}
                  {ticket.resolutionSummary && (
                    <div className={detailStyles.descriptionSection}>
                      <h3 className={detailStyles.sectionTitle}>
                        {strings.ticketResolutionSummaryLabel}
                        {ticket.translatedResolutionSummary &&
                          hasTranslation(ticket.resolutionTranslationStatus) && (
                            <TranslationBadge
                              status={ticket.resolutionTranslationStatus}
                              showingOriginal={showOriginalResolution}
                              onToggle={() => setShowOriginalResolution(prev => !prev)}
                              strings={strings}
                            />
                          )}
                      </h3>
                      {(() => {
                        const useTranslated =
                          !showOriginalResolution &&
                          !!ticket.translatedResolutionSummary &&
                          hasTranslation(ticket.resolutionTranslationStatus);
                        const displayText = useTranslated
                          ? ticket.translatedResolutionSummary!
                          : ticket.resolutionSummary ?? "";
                        return isHtml(displayText) ? (
                          <div
                            className={detailStyles.descriptionHtml}
                            dangerouslySetInnerHTML={{ __html: displayText }}
                          />
                        ) : (
                          <p className={detailStyles.descriptionText}>{displayText}</p>
                        );
                      })()}
                    </div>
                  )}

                  {/* Tags */}
                  <div className={detailStyles.tagsSection}>
                    <h3 className={detailStyles.sectionTitle}>{strings.ticketTagsLabel}</h3>
                    <div
                      className={detailStyles.tagsList}
                      role="group"
                      aria-label={strings.ticketTagsLabel}
                    >
                      {localTags.length === 0 && isClosed && (
                        <p className={detailStyles.emptyMessage}>{strings.ticketNoTagsMsg}</p>
                      )}
                      {localTags.map((t, i) => (
                        <span key={i} className={detailStyles.tag}>
                          {t}
                          {!isClosed && (
                            <button
                              className={detailStyles.tagRemove}
                              onClick={() => handleRemoveTag(i)}
                              aria-label={strings.ticketRemoveTagAriaLabel.replace("{0}", t)}
                              disabled={isUpdating}
                            >
                              ✕
                            </button>
                          )}
                        </span>
                      ))}
                      {!isClosed && (
                        <input
                          type="text"
                          className={detailStyles.addTagInput}
                          value={newTag}
                          onChange={e => setNewTag(e.target.value)}
                          onKeyDown={handleTagKeyDown}
                          placeholder={strings.ticketTagsPlaceholder}
                          maxLength={50}
                          disabled={isUpdating}
                          aria-label={strings.ticketTagInputAriaLabel}
                        />
                      )}
                      {!isClosed && (
                        <HeroButton
                          as="button"
                          className={`${detailStyles.saveTagsButton} ${commonStyles.actionButton}`}
                          onClick={handleSaveTags}
                          disabled={!tagsModified || isUpdating}
                        >
                          {strings.ticketSaveTagsLabel}
                        </HeroButton>
                      )}
                    </div>
                  </div>

                  {/* General Info */}
                  <h3 className={detailStyles.sectionTitle}>{strings.ticketGeneralInfoLabel}</h3>
                  <div className={detailStyles.infoBox}>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>
                        {strings.ticketDetailStatusLabel}
                      </span>
                      <div className={detailStyles.statusRow}>
                        <span className={detailStyles.infoValue}>
                          {translateStatusLabel(translationMap, ticket.status) ||
                            strings.ticketNaLabel}
                        </span>
                        {isResolved && (
                          <HeroButton
                            as="button"
                            buttonType={ButtonType.Secondary}
                            className={detailStyles.closeTicketButton}
                            onClick={handleCloseTicket}
                            disabled={isUpdating}
                          >
                            {isClosing
                              ? strings.ticketClosingLabel
                              : strings.ticketCloseTicketLabel}
                          </HeroButton>
                        )}
                      </div>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>
                        {strings.ticketDetailStatusDateLabel}
                      </span>
                      <span className={detailStyles.infoValue}>
                        {(ticket.status === TicketStatusLabel.Active
                          ? ticket.firstTriageDate
                          : ticket.status === TicketStatusLabel.Resolved
                          ? ticket.resolvedDate
                          : ticket.status === TicketStatusLabel.Closed
                          ? ticket.closedDate
                          : undefined) || strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>
                        {strings.ticketDetailCreatedLabel}
                      </span>
                      <span className={detailStyles.infoValue}>
                        {ticket.createdOn || strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>{strings.ticketSeverityLabel}</span>
                      <span className={detailStyles.infoValue}>
                        {ticket.severity
                          ? translateSeverityLabel(translationMap, ticket.severity)
                          : strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>{strings.ticketCategoryLabel}</span>
                      <span className={detailStyles.infoValue}>
                        {ticket.category
                          ? translateValue(
                              translationMap,
                              TranslationEntity.product,
                              TranslationField.name,
                              ticket.categoryId ?? "",
                              ticket.category
                            )
                          : strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>
                        {strings.ticketSubCategoryLabel}
                      </span>
                      <span className={detailStyles.infoValue}>
                        {ticket.subCategory
                          ? translateValue(
                              translationMap,
                              TranslationEntity.productFeature,
                              TranslationField.name,
                              ticket.subCategoryId ?? "",
                              ticket.subCategory
                            )
                          : strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>
                        {strings.ticketDetailSubmittedByLabel}
                      </span>
                      <span className={detailStyles.infoValue}>
                        {ticket.submittedBy || strings.ticketNaLabel}
                      </span>
                    </div>
                    <div className={detailStyles.infoItem}>
                      <span className={detailStyles.infoLabel}>{strings.ticketTeamLabel}</span>
                      <span className={detailStyles.infoValue}>
                        {ticket.teamName || strings.ticketTeamPrivateOption}
                      </span>
                    </div>
                  </div>

                  {/* Team Scope (Admin only) */}
                  {isAdmin && (
                    <>
                      <h3 className={detailStyles.sectionTitle}>{strings.ticketTeamScopeTitle}</h3>
                      <div className={detailStyles.teamScopeSection}>
                        <div className={detailStyles.teamScopeRow}>
                          <span className={detailStyles.teamScopeLabel}>
                            {strings.ticketTeamScopeCurrentLabel}
                          </span>
                          <span className={detailStyles.teamScopeBadge}>
                            {ticket.teamName || strings.ticketTeamPrivateOption}
                          </span>
                        </div>
                        <div className={detailStyles.teamScopeRow}>
                          <label
                            htmlFor="team-scope-select"
                            className={detailStyles.teamScopeLabel}
                          >
                            {strings.ticketTeamScopeChangeLabel}
                          </label>
                          <SelectDropdown
                            className={detailStyles.teamScopeSelect}
                            value={selectedTeamId}
                            onChange={setSelectedTeamId}
                            disabled={isSavingTeamScope}
                            options={[
                              { id: "", label: "Select new team" },
                              ...teams
                                .filter(t => !t.isFrozen)
                                .map(t => ({ id: t.id, label: t.name })),
                            ]}
                            aria-label={strings.ticketTeamScopeChangeLabel}
                          />
                          <HeroButton
                            as="button"
                            className={`${detailStyles.teamScopeSaveButton} ${commonStyles.actionButton}`}
                            onClick={handleSaveTeamScope}
                            disabled={
                              isSavingTeamScope ||
                              !selectedTeamId ||
                              selectedTeamId === ticket.teamId
                            }
                          >
                            {isSavingTeamScope
                              ? strings.ticketTeamScopeSavingButton
                              : strings.ticketTeamScopeSaveButton}
                          </HeroButton>
                        </div>
                        <p className={detailStyles.teamScopeHelp}>{strings.ticketTeamScopeHelp}</p>
                      </div>
                    </>
                  )}

                  {/* Attachments */}
                  <div className={detailStyles.attachmentsSection}>
                    <h3 className={detailStyles.sectionTitle}>{strings.ticketRelatedLinksLabel}</h3>
                    {visibleAttachments.length === 0 && pendingAddAttachments.length === 0 ? (
                      <p className={detailStyles.emptyMessage}>{strings.ticketNoLinksMsg}</p>
                    ) : (
                      <div className={detailStyles.attachmentList}>
                        {visibleAttachments.map(att => (
                          <div key={att.id} className={detailStyles.attachmentItem}>
                            <div className={detailStyles.attachmentInfo}>
                              <a
                                href={
                                  att.url.match(/^https?:\/\//) ? att.url : `https://${att.url}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className={detailStyles.attachmentLink}
                              >
                                {att.url}
                              </a>
                              {att.description && (
                                <span className={detailStyles.attachmentDesc}>
                                  {att.description}
                                </span>
                              )}
                            </div>
                            <button
                              className={detailStyles.attachmentRemove}
                              onClick={() => handleRemoveExistingAttachment(att.id)}
                              aria-label={strings.ticketRemoveItemAriaLabel.replace(
                                "{0}",
                                att.description || att.url
                              )}
                              disabled={isUpdating}
                              hidden={isClosed}
                            >
                              ✕
                            </button>
                          </div>
                        ))}
                        {pendingAddAttachments
                          .filter(att => !ticket.attachments.some(a => a.url === att.url))
                          .map((att, i) => (
                            <div key={`new-${i}`} className={detailStyles.attachmentItem}>
                              <div className={detailStyles.attachmentInfo}>
                                <span className={detailStyles.attachmentLink}>{att.url}</span>
                                {att.description && (
                                  <span className={detailStyles.attachmentDesc}>
                                    {att.description}
                                  </span>
                                )}
                              </div>
                              <button
                                className={detailStyles.attachmentRemove}
                                onClick={() => handleRemovePendingAdd(i)}
                                aria-label={strings.ticketRemoveItemAriaLabel.replace(
                                  "{0}",
                                  att.url
                                )}
                                disabled={isUpdating}
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                      </div>
                    )}
                    {/* Undo removed */}
                    {pendingRemoveIds.length > 0 && (
                      <div className={detailStyles.undoList}>
                        {pendingRemoveIds.map(id => {
                          const att = ticket.attachments.find(a => a.id === id);
                          const attName = att?.name || att?.url || "";
                          return (
                            <span key={id} className={detailStyles.undoItem}>
                              <span className={detailStyles.undoText}>{attName}</span>
                              <button
                                className={detailStyles.undoButton}
                                onClick={() => handleUndoRemoveAttachment(id)}
                                aria-label={strings.ticketUndoRemoveAriaLabel.replace(
                                  "{0}",
                                  attName
                                )}
                              >
                                {strings.ticketUndoLabel}
                              </button>
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {!isClosed && (
                      <div className={detailStyles.addAttachmentRow}>
                        <div className={detailStyles.addAttachmentField}>
                          <input
                            type="text"
                            className={detailStyles.addAttachmentInput}
                            value={newAttachmentUrl}
                            onChange={e => setNewAttachmentUrl(e.target.value)}
                            placeholder={strings.ticketLinkUrlPlaceholder}
                            maxLength={1000}
                            disabled={isUpdating}
                            aria-label={strings.ticketAttachmentUrlAriaLabel.replace("{0}", "")}
                          />
                          {newAttachmentUrl.length >= 800 && (
                            <span
                              className={cx(detailStyles.charCount, {
                                [detailStyles.charCountWarning]: newAttachmentUrl.length >= 1000,
                              })}
                              aria-live="polite"
                            >
                              {newAttachmentUrl.length}/1000
                            </span>
                          )}
                        </div>
                        <div className={detailStyles.addAttachmentField}>
                          <input
                            type="text"
                            className={detailStyles.addAttachmentInput}
                            value={newAttachmentDesc}
                            onChange={e => setNewAttachmentDesc(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === "Enter") {
                                e.preventDefault();
                                handleAddPendingAttachment();
                              }
                            }}
                            placeholder={strings.ticketLinkDescPlaceholder}
                            maxLength={800}
                            disabled={isUpdating}
                            aria-label={strings.ticketAttachmentDescAriaLabel.replace("{0}", "")}
                          />
                          {newAttachmentDesc.length >= 640 && (
                            <span
                              className={cx(detailStyles.charCount, {
                                [detailStyles.charCountWarning]: newAttachmentDesc.length >= 800,
                              })}
                              aria-live="polite"
                            >
                              {newAttachmentDesc.length}/800
                            </span>
                          )}
                        </div>
                        <button
                          className={cx(detailStyles.addAttachmentButton, {
                            [detailStyles.addAttachmentButtonDisabled]:
                              !newAttachmentUrl.trim() ||
                              newAttachmentUrl.trim() === "https://" ||
                              isUpdating,
                          })}
                          onClick={() => {
                            if (
                              !newAttachmentUrl.trim() ||
                              newAttachmentUrl.trim() === "https://" ||
                              isUpdating
                            ) {
                              return;
                            }
                            handleAddPendingAttachment();
                          }}
                          aria-disabled={
                            !newAttachmentUrl.trim() ||
                            newAttachmentUrl.trim() === "https://" ||
                            isUpdating
                          }
                          aria-label={strings.ticketAddAttachmentAriaLabel}
                        >
                          {strings.ticketAddLinkLabel}
                        </button>
                      </div>
                    )}
                    {!isClosed && (
                      <HeroButton
                        as="button"
                        className={`${detailStyles.saveAttachmentsButton} ${commonStyles.actionButton}`}
                        onClick={handleSaveAttachments}
                        disabled={!attachmentsModified || isUpdating}
                      >
                        {strings.ticketSaveLinksLabel}
                      </HeroButton>
                    )}
                  </div>

                  {/* Comments */}
                  <div className={detailStyles.commentsSection}>
                    <h3 id="detail-comments-heading" className={detailStyles.sectionTitle}>
                      {strings.ticketCommentsLabel}
                    </h3>
                    {!isClosed && (
                      <div className={detailStyles.commentInput}>
                        <div
                          ref={commentEditorRef}
                          className={detailStyles.commentRichTextEditor}
                          role="group"
                          aria-labelledby="detail-comments-heading"
                        >
                          {ReactQuill && (
                            <ReactQuill
                              theme="snow"
                              value={commentText}
                              onChange={handleCommentChange}
                              modules={commentQuillModules}
                              formats={commentQuillFormats}
                              readOnly={isAddingComment}
                              placeholder={strings.ticketCommentPlaceholder}
                            />
                          )}
                        </div>
                        {commentTextLength >= 16000 && (
                          <span
                            className={cx(detailStyles.charCount, {
                              [detailStyles.charCountWarning]: commentTextLength >= 20000,
                            })}
                            aria-live="polite"
                          >
                            {commentTextLength}/20000
                          </span>
                        )}
                        <HeroButton
                          as="button"
                          className={`${detailStyles.commentSendButton} ${commonStyles.actionButton}`}
                          onClick={handleSendComment}
                          disabled={isAddingComment || commentTextLength === 0}
                        >
                          {isAddingComment
                            ? strings.ticketSendingLabel
                            : strings.ticketAddCommentLabel}
                        </HeroButton>
                      </div>
                    )}
                    {ticket.comments.length === 0 ? (
                      <p className={detailStyles.emptyMessage}>{strings.ticketNoCommentsMsg}</p>
                    ) : (
                      <div className={detailStyles.commentsList}>
                        {ticket.comments.map(c => {
                          const isMe =
                            c.authorType === TicketCommentAuthor.User &&
                            (!c.authorName || c.authorName === ticket.submittedBy);
                          const isAdminComment = c.authorType === TicketCommentAuthor.Admin;
                          return (
                            <div
                              key={c.id}
                              className={cx(detailStyles.commentBubble, {
                                [detailStyles.commentUser]: isMe,
                                [detailStyles.commentAdmin]: isAdminComment,
                                [detailStyles.commentOtherUser]: !isMe && !isAdminComment,
                              })}
                            >
                              <div className={detailStyles.commentMeta}>
                                {c.authorName
                                  ? c.authorName
                                  : c.authorType === TicketCommentAuthor.Admin
                                  ? strings.ticketSupportAuthor
                                  : strings.ticketCustomerAuthor}{" "}
                                · {formatLocalDateTime(c.createdOn)}
                                {!isMe &&
                                  c.translatedComment &&
                                  hasTranslation(c.translationStatus) && (
                                    <TranslationBadge
                                      status={c.translationStatus}
                                      showingOriginal={!!showOriginalComments[c.id]}
                                      onToggle={() =>
                                        setShowOriginalComments(prev => ({
                                          ...prev,
                                          [c.id]: !prev[c.id],
                                        }))
                                      }
                                      strings={strings}
                                    />
                                  )}
                              </div>
                              {(() => {
                                const useTranslated =
                                  !isMe &&
                                  !showOriginalComments[c.id] &&
                                  !!c.translatedComment &&
                                  hasTranslation(c.translationStatus);
                                const displayText = useTranslated
                                  ? c.translatedComment!
                                  : c.comment;
                                return isHtml(displayText) ? (
                                  <div
                                    className={detailStyles.commentHtml}
                                    dangerouslySetInnerHTML={{ __html: displayText }}
                                  />
                                ) : (
                                  <div className={detailStyles.commentText}>{displayText}</div>
                                );
                              })()}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default TicketingTicketDetailPage;
